import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import createAgentNote from '@/shared/api/notes/agentNotes/createAgentNote';
import viewAgentNotes from '@/shared/api/notes/agentNotes/viewAgentNotes';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import AgentNote, {
  CreateAgentNote,
} from '@/shared/types/notes/agentNote.type';
import getSearchBodyFilters from '@/shared/utils/getSearchBodyFilters';

import NoteModule from '../types/noteModule.type';
import TimelineSearchBoxOption from '../types/timelineSearchBoxOption.type';

type Args = {
  customerId: string;
  module: NoteModule;
  moduleItemId: string;
};

const useAgentNotes = ({ customerId, module, moduleItemId }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [agentNotes, setAgentNotes] = useState<AgentNote[][] | null>(null);
  const [options, setOptions] = useState<TimelineSearchBoxOption[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const initialFilters = useMemo(
    () => [
      {
        key: 'in_customer_id',
        value: customerId,
        cop: '=',
      },
      {
        key: 'module_ref',
        value: module,
        cop: '=',
      },
    ],
    [customerId, module],
  );

  const sortAsc = (a: AgentNote, b: AgentNote) => {
    if (a.ts_created > b.ts_created) {
      return 1;
    }
    if (a.ts_created < b.ts_created) {
      return -1;
    }
    return 0;
  };

  const sortDescByFirstItemDate = (a: AgentNote[], b: AgentNote[]) => {
    if (a[0].ts_created > b[0].ts_created) {
      return -1;
    }
    if (a[0].ts_created < b[0].ts_created) {
      return 1;
    }
    return 0;
  };

  const getGroupedNotes = useCallback((notes: AgentNote[]) => {
    const groups = new Map<string, AgentNote[]>();

    notes.forEach((note) => {
      const key = note.conversation_id;
      groups.has(key) ? groups.get(key)?.push(note) : groups.set(key, [note]);
    });

    return [...groups.values()]
      .map((group) => group.sort(sortAsc))
      .sort(sortDescByFirstItemDate);
  }, []);

  useEffect(() => {
    const fetchAgentNotes = async () => {
      const response = await viewAgentNotes({
        body: {
          search: getSearchBodyFilters({
            mode: 'multipleValuesMultipleKeys',
            options: initialFilters,
            op: 'and',
          }),
        },
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setAgentNotes(getGroupedNotes(response.data));
      } else {
        console.error(
          'Error occurred while fetching agent notes:',
          response.msg,
        );
      }
    };

    void fetchAgentNotes();
  }, [customerId, getGroupedNotes, initialFilters, redirectLogin]);

  useEffect(() => {
    if (agentNotes && agentNotes.length > 0) {
      setOptions(
        Object.keys(agentNotes[0][0])
          .filter((item) => {
            const requiredFields: Partial<keyof AgentNote>[] = [
              'module_item_id',
              'module_ref',
              'note',
              'note_id',
              'ts_created',
              'ts_modified',
              'user_email_address',
              'user_first_name',
              'user_last_name',
            ];

            return requiredFields.includes(item as keyof AgentNote);
          })
          .map((key) => ({
            value: key,
            label: key,
            filters: [],
          })),
      );
    }
  }, [agentNotes]);

  const handleApplyFilters = async (filters: TimelineSearchBoxOption[]) => {
    const filtersWithValues = filters
      .filter(
        (filter) =>
          filter.filters.length > 0 &&
          filter.filters.some((f) => f.value !== ''),
      )
      .reduce(
        (acc, filter) => {
          const filters = filter.filters.map((f) => ({
            key: filter.value,
            value: f.value,
            cop: f.cop,
          }));

          return [...acc, ...filters];
        },
        [] as TimelineSearchBoxOption['filters'] & { key: string }[],
      );

    const response = await viewAgentNotes({
      body: {
        search: getSearchBodyFilters({
          mode: 'multipleValuesMultipleKeys',
          options: [...initialFilters, ...filtersWithValues],
          op: 'and',
        }),
      },
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      setAgentNotes(getGroupedNotes(response.data));
    } else {
      console.error('Error occurred while fetching agent notes:', response.msg);
      setErrors([response.msg]);
    }
  };

  const handleCreateAgentNote = async (note: string) => {
    const requestBody: CreateAgentNote = {
      module_ref: module,
      module_item_id: moduleItemId,
      in_customer_id: customerId,
      conversation_id: uuidv4(),
      note,
    };

    const response = await createAgentNote({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      const newNotes = [...response.data, ...(agentNotes?.flat() || [])];
      setAgentNotes(getGroupedNotes(newNotes));
    } else {
      console.error('Error occurred while creating agent note:', response.msg);
      setErrors([response.msg]);
    }
  };

  const handleReply = async (note: string, conversationID: string) => {
    const requestBody: CreateAgentNote = {
      module_ref: module,
      module_item_id: moduleItemId,
      in_customer_id: customerId,
      conversation_id: conversationID,
      note,
    };

    const response = await createAgentNote({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      const newNotes = [...response.data, ...(agentNotes?.flat() || [])];
      setAgentNotes(getGroupedNotes(newNotes));
    } else {
      console.error('Error occurred while creating agent note:', response.msg);
      setErrors([response.msg]);
    }
  };

  return {
    errors,
    options,
    agentNotes,
    handleApplyFilters,
    handleCreateAgentNote,
    handleReply,
  };
};

export default useAgentNotes;
